import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Assignment as VatIcon,
  CloudUpload as CloudUploadIcon,
  Description as InvoiceIcon,
  Payments as PaymentsIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import FinanceContainer from "../containers/FinanceContainer";
import ImportDialog from "../components/ImportDialog";

type ImportType =
  | "payments"
  | "receipts"
  | "debtor-invoices"
  | "vat-invoices"
  | "vat-payments"
  | "take-on-transactions";

interface ImportOption {
  type: ImportType;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const FinanceImport: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<ImportType>("payments");
  const [refreshKey, setRefreshKey] = useState(0);

  const importOptions: ImportOption[] = [
    {
      type: "payments",
      title: "Import Payments",
      description: "Import debtor payment lines for installment and receipt allocation workflows.",
      icon: <PaymentsIcon sx={{ fontSize: 40 }} />,
    },
    {
      type: "receipts",
      title: "Import Receipts",
      description: "Import captured receipts that should update debtor balances and aging.",
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
    },
    {
      type: "debtor-invoices",
      title: "Import Debtor Invoices",
      description: "Import installment or VAT-related invoices raised against debtors.",
      icon: <InvoiceIcon sx={{ fontSize: 40 }} />,
    },
    {
      type: "vat-invoices",
      title: "Import VAT Invoices",
      description: "Import VAT output or adjustment invoices for tax tracking.",
      icon: <VatIcon sx={{ fontSize: 40 }} />,
    },
    {
      type: "vat-payments",
      title: "Import VAT Payments",
      description: "Import VAT remittances and reconciled VAT payment records.",
      icon: <VatIcon sx={{ fontSize: 40 }} />,
    },
    {
      type: "take-on-transactions",
      title: "Import Take-On Transactions",
      description: "Import opening invoices, payments, receipts, and VAT movements from one template.",
      icon: <CloudUploadIcon sx={{ fontSize: 40 }} />,
    },
  ];

  const handleImportClick = (type: ImportType) => {
    setSelectedType(type);
    setOpenDialog(true);
  };

  const handleImportComplete = () => {
    setRefreshKey((prev) => prev + 1);
    setOpenDialog(false);
  };

  return (
    <FinanceContainer>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Import Finance Data
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Use this page for bulk finance capture, historical take-on imports, and VAT or installment transaction loading.
        </Alert>

        <Grid container spacing={3}>
          {importOptions.map((option) => (
            <Grid item xs={12} sm={6} md={4} key={option.type}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardHeader
                  avatar={<Box sx={{ color: "primary.main" }}>{option.icon}</Box>}
                  title={option.title}
                  titleTypographyProps={{ variant: "h6" }}
                  sx={{ pb: 1 }}
                />
                <Divider />
                <CardContent
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, flexGrow: 1 }}
                  >
                    {option.description}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => handleImportClick(option.type)}
                    fullWidth
                  >
                    Open Import
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ mt: 4, p: 3, backgroundColor: "background.default" }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Import Guidelines
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              • <strong>Download the template:</strong> Each import dialog provides the correct columns for that capture type.
            </Typography>
            <Typography variant="body2">
              • <strong>Prefer Excel:</strong> Use <code>.xlsx</code> or <code>.xls</code> for the most reliable import formatting.
            </Typography>
            <Typography variant="body2">
              • <strong>Use take-on for history:</strong> Historical balances and prior-period receipts should go through the take-on template.
            </Typography>
            <Typography variant="body2">
              • <strong>Keep debtor IDs valid:</strong> Debtor-linked imports will fail if the debtor record does not already exist.
            </Typography>
            <Typography variant="body2">
              • <strong>Allocation matters:</strong> Provide invoice references where possible so payments can be matched cleanly.
            </Typography>
            <Typography variant="body2">
              • <strong>Review row errors:</strong> The importer returns row numbers for any failed records so you can fix only what failed.
            </Typography>
          </Stack>
        </Paper>

        <ImportDialog
          key={`import-${selectedType}-${refreshKey}`}
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          type={selectedType}
          onImportComplete={handleImportComplete}
        />
      </Box>
    </FinanceContainer>
  );
};

export default FinanceImport;
